import { CharStreams, CommonTokenStream } from 'antlr4';
import AioExpressionLexer from '../anltr/AioExpressionLexer.js';
import AioExpressionParser from '../anltr/AioExpressionParser.js';
import AioExpressionEvalVisitor from './AioExpressionEvalVisitor.js';
import AioExpressionParsingException from '../exception/AioExpressionParsingException.js';
import CustomErrorListener from '../listeners/ErrorListener.js';
/**
 * A parser for the AIO expression language that can evaluate expressions
 * and calculate boolean or string results.
 */
export default class AioExpressionLanguageParser {
    /**
     * Parses and evaluates an AIO expression.
     *
     * @param input - The input string to parse and evaluate.
     * @param variables - An optional array of variable definitions (default is an empty array).
     * @param wrapCommand - Specifies whether to wrap the input in double curly braces (default is false).
     * @returns The evaluated result as an object.
     * @throws {AioExpressionParsingException} If there is an error during parsing or evaluation.
     */
    parseInput(input, variables = [], wrapCommand = false) {
        const expression = wrapCommand ? `{{${input}}}` : input;
        const lexer = new AioExpressionLexer(CharStreams.fromString(expression));
        lexer.removeErrorListeners();
        lexer.addErrorListener(new CustomErrorListener());
        const parser = new AioExpressionParser(new CommonTokenStream(lexer));
        const tree = parser.input();
        const combinedStringEvalVisitor = new AioExpressionEvalVisitor();
        const vars = new Map();
        for (const variable of variables) {
            const [name, value] = variable.split('=');
            vars.set(name, value.startsWith('[') ? value.slice(1, -1).split(',') : value);
        }
        return combinedStringEvalVisitor.setVariables(vars).visit(tree);
    }
    /**
     * Calculates a boolean result from the input string.
     *
     * @param input - The input string to parse and calculate.
     * @returns The calculated result as a boolean.
     * @throws {AioExpressionParsingException} If the input string doesn't result in a boolean.
     */
    calculateBoolean(input, variables) {
        const result = this.parseInput(input, variables, true).toString();
        if (result === 'true')
            return true;
        else if (result === 'false')
            return false;
        else
            throw new AioExpressionParsingException("Method didn't result with boolean");
    }
    /**
     * Parses and calculates the result of an result string with optional variables.
     *
     * @param input - The input string to parse and calculate.
     * @param variables - An optional array of variable names (default is undefined).
     * @returns The calculated result as a string.
     */
    calculateString(input, variables) {
        return this.parseInput(input, variables, false).toString();
    }
}
